import { useEffect, useRef, useState } from 'react'
import type { SlamJson } from '../../robot-position-setup/types'

const EMPTY_SLAM_JSON: SlamJson = {
  bounds: { maxX: 0, maxY: 0, minX: 0, minY: 0 },
  obstacles: [],
  robot: {},
}

interface UseSlamMapStateOptions {
  mapFileUrl?: string
  activeTab: string
  isLoadingPositions: boolean
  onLoadError?: () => void
}

export function useSlamMapState({
  mapFileUrl,
  activeTab,
  isLoadingPositions,
  onLoadError,
}: UseSlamMapStateOptions) {
  const [slamJson, setSlamJson] = useState<SlamJson>(EMPTY_SLAM_JSON)
  const [isSlamLoading, setIsSlamLoading] = useState(false)
  const [isCanvasSettled, setIsCanvasSettled] = useState(false)
  const [hasSlamLoadError, setHasSlamLoadError] = useState(false)
  const onLoadErrorRef = useRef(onLoadError)

  useEffect(() => {
    onLoadErrorRef.current = onLoadError
  }, [onLoadError])

  const hasMap = !!mapFileUrl
  const isPositionSetupLoading = !hasMap || isSlamLoading || isLoadingPositions
  const showPositionSetupContent =
    !isPositionSetupLoading && isCanvasSettled && !hasSlamLoadError
  useEffect(() => {
    if (activeTab !== '2' || isPositionSetupLoading) {
      setIsCanvasSettled(false)
      return
    }

    setIsCanvasSettled(false)
    const timer = window.setTimeout(() => {
      setIsCanvasSettled(true)
    }, 120)

    return () => {
      window.clearTimeout(timer)
    }
  }, [activeTab, isPositionSetupLoading])

  const notifiedErrorForMapRef = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false

    // Reset error-notification lock when the map URL changes
    notifiedErrorForMapRef.current = null

    const run = async () => {
      if (!mapFileUrl) {
        setHasSlamLoadError(false)
        setSlamJson(EMPTY_SLAM_JSON)
        setIsSlamLoading(false)
        return
      }

      setHasSlamLoadError(false)
      setIsSlamLoading(true)
      try {
        const res = await fetch(mapFileUrl)

        if (!res.ok) {
          throw new Error(`Slam map fetch failed with status ${res.status}`)
        }

        const contentType = res.headers.get('content-type')
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Định dạng phản hồi không phải là JSON hợp lệ (Có thể là HTML/XML lỗi)')
        }

        const data = (await res.json()) as SlamJson

        if (!cancelled) setSlamJson(data)
      } catch (error) {
        if (!cancelled) {
          setHasSlamLoadError(true)
          if (notifiedErrorForMapRef.current !== mapFileUrl) {
            notifiedErrorForMapRef.current = mapFileUrl
            onLoadErrorRef.current?.()
          }
        }
      } finally {
        if (!cancelled) setIsSlamLoading(false)
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [mapFileUrl])



  return {
    slamJson,
    isSlamLoading,
    hasMap,
    hasSlamLoadError,
    isPositionSetupLoading,
    showPositionSetupContent,
  }
}
